import React from 'react';
import { useField, useFormikContext } from 'formik';
import { SuggestedFollowUpActionFields, SuggestedFollowUpValue } from '@uplift/api';
import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTimezone } from '@uplift/contexts';
import { CalendarDateService } from '@uplift/services';
import { DateFormats } from '@uplift/constants';
import { useL10n } from '@uplift/hooks';
import { Button } from '../../Button';
import { useFollowUpScheduler } from './FollowUpScheduler.context';

const MEDICATION_ACTION_FOLLOW_UP_DAYS = 14;
const CONTINUE_ONLY_FOLLOW_UP_DAYS = 60;

const useStyles = makeStyles()(theme => ({
  root: {
    backgroundColor: theme.palette.info.background,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dateRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  uppercaseButton: {
    textTransform: 'uppercase',
  },
  disclosureBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
}));


export type SuggestedFollowUpProps = {
  name: string;
  planActionFields: SuggestedFollowUpActionFields;
  sessionStartDate?: string | null;
};


export const SuggestedFollowUp: React.FC<SuggestedFollowUpProps> = ({ name, planActionFields, sessionStartDate }) => {
  const { classes } = useStyles();
  const l10n = useL10n();
  const timezone = useTimezone();
  const { values } = useFormikContext<Record<string, unknown>>();
  const namespace = name.includes('.') ? name.slice(0, name.lastIndexOf('.')) : undefined;
  const [ field, , helpers ] = useField<SuggestedFollowUpValue | null>(name);
  const storedDate = field.value?.date;
  const [ disclosureOpen, setDisclosureOpen ] = React.useState(false);
  const followUpScheduler = useFollowUpScheduler();
  const scheduledFollowUpDate = followUpScheduler?.scheduledFollowUpDate;

  const getSiblingValue = (fieldId: string) => {
    return !!(namespace ? (values[namespace] as Record<string, unknown>)?.[fieldId] : values[fieldId]);
  };

  const checkedActions = React.useMemo(() => {
    const actionFieldIds = {
      continue: planActionFields.continueField,
      start: planActionFields.startField,
      stop: planActionFields.stopField,
      change: planActionFields.changeField,
    };

    return (Object.keys(actionFieldIds) as (keyof typeof actionFieldIds)[])
      .filter(action => getSiblingValue(actionFieldIds[action]));
  }, [ values, namespace, planActionFields ]);

  const medicationActions = checkedActions.filter(action => action !== 'continue');
  const isMedicationAction = medicationActions.length > 0;
  let suggestedFollowUpDays: number | null = null;

  if (isMedicationAction) {
    suggestedFollowUpDays = MEDICATION_ACTION_FOLLOW_UP_DAYS;
  } else if (checkedActions.length > 0) {
    suggestedFollowUpDays = CONTINUE_ONLY_FOLLOW_UP_DAYS;
  }

  let rationale: string = l10n.clinicalNotes.suggestedFollowUp.rationaleMaintenance;

  if (checkedActions.includes('start')) {
    rationale = l10n.clinicalNotes.suggestedFollowUp.rationaleInitiation;
  } else if (checkedActions.includes('change')) {
    rationale = l10n.clinicalNotes.suggestedFollowUp.rationaleTitration;
  } else if (checkedActions.includes('stop')) {
    rationale = l10n.clinicalNotes.suggestedFollowUp.rationaleDiscontinuation;
  }

  let planStatement: string = l10n.clinicalNotes.suggestedFollowUp.continuing;

  if (medicationActions.length > 1) {
    planStatement = l10n.clinicalNotes.suggestedFollowUp.changes;
  } else if (medicationActions.includes('stop')) {
    planStatement = l10n.clinicalNotes.suggestedFollowUp.stopped;
  } else if (isMedicationAction) {
    planStatement = l10n.clinicalNotes.suggestedFollowUp.started;
  }

  const summary = `${planStatement} ${l10n.clinicalNotes.suggestedFollowUp.recommendedCheckIn(
    isMedicationAction ? l10n.clinicalNotes.suggestedFollowUp.intervalTwoWeeks : l10n.clinicalNotes.suggestedFollowUp.intervalSixtyDays
  )}`;

  // Re-read in the timezone so an interval crossing daylight saving keeps its time of day.
  const addDaysInTimezone = (days: number) => {
    const start = (sessionStartDate ? CalendarDateService.date(sessionStartDate) : CalendarDateService.date()).tz(timezone);

    return CalendarDateService.date.tz(start.add(days, 'day').format('YYYY-MM-DDTHH:mm:ss'), timezone);
  };

  React.useEffect(() => {
    if (suggestedFollowUpDays === null) {
      helpers.setValue({ days: null, date: null });
      return;
    }

    helpers.setValue({
      days: suggestedFollowUpDays,
      date: addDaysInTimezone(suggestedFollowUpDays).format(DateFormats.ISODayjs),
    });
  }, [ suggestedFollowUpDays, sessionStartDate ]);

  if (checkedActions.length === 0 || suggestedFollowUpDays === null) {
    return null;
  }

  let followUpDate = addDaysInTimezone(suggestedFollowUpDays);

  if (scheduledFollowUpDate) {
    followUpDate = CalendarDateService.date(scheduledFollowUpDate).tz(timezone);
  } else if (storedDate) {
    followUpDate = CalendarDateService.date(storedDate).tz(timezone);
  }

  const followUpDateLabel = followUpDate.format(`${DateFormats['MMM D, YYYY']} · h:mm A`);
  const openScheduler = () => followUpScheduler?.onOpen(followUpDate.format(DateFormats.ISODayjs));
  const openSessionDetails = () => followUpScheduler?.onOpenSessionDetails(followUpDate.format(DateFormats.ISODayjs));

  return (
    <Box className={classes.root}>
      {scheduledFollowUpDate ? (
        <Box className={classes.dateRow}>
          <Typography fontWeight={500}>
            {l10n.clinicalNotes.suggestedFollowUp.scheduled(followUpDateLabel)}
          </Typography>
          {followUpScheduler && (
            <Button
              size="small"
              color="secondary"
              className={classes.uppercaseButton}
              onClick={openScheduler}
            >
              {l10n.clinicalNotes.suggestedFollowUp.change}
            </Button>
          )}
        </Box>
      ) : (
        <>
          <Typography fontWeight={500}>{l10n.clinicalNotes.suggestedFollowUp.title}</Typography>

          <Typography>{summary}</Typography>

          <Box className={classes.dateRow}>
            <Button
              variant="outlined"
              color="secondary"
              disabled={!followUpScheduler}
              onClick={openSessionDetails}
            >
              {followUpDateLabel}
            </Button>
            {followUpScheduler && (
              <Button
                size="small"
                color="secondary"
                onClick={openScheduler}
              >
                {l10n.clinicalNotes.suggestedFollowUp.anotherTime}
              </Button>
            )}
          </Box>

          <Box display="flex">
            <Button
              size="small"
              color="secondary"
              className={classes.uppercaseButton}
              onClick={() => setDisclosureOpen(open => !open)}
            >
              {disclosureOpen ? l10n.clinicalNotes.suggestedFollowUp.hide : l10n.clinicalNotes.suggestedFollowUp.whyThisInterval}
            </Button>
          </Box>
        </>
      )}

      {disclosureOpen && !scheduledFollowUpDate && (
        <Box className={classes.disclosureBody}>
          <Typography variant="body2">{rationale}</Typography>
          <Box className={classes.row}>
            <Typography variant="caption" color="textSecondary">{l10n.clinicalNotes.suggestedFollowUp.reference}</Typography>
            <Box display="flex" gap={1}>
              <Button size="small" color="secondary" href={l10n.clinicalNotes.suggestedFollowUp.referenceVaDodUrl} target="_blank" rel="noreferrer">
                {l10n.clinicalNotes.suggestedFollowUp.referenceVaDodLabel}
              </Button>
              <Typography color="textSecondary">·</Typography>
              <Button size="small" color="secondary" href={l10n.clinicalNotes.suggestedFollowUp.referenceNiceUrl} target="_blank" rel="noreferrer">
                {l10n.clinicalNotes.suggestedFollowUp.referenceNiceLabel}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};
